import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Activity as ActivityIcon, Search, Filter, Download,
    Calendar, User, Users, Eye, UserPlus, UserCheck, Lock,
    Mail, Settings, Shield, ChevronLeft, ChevronRight,
    RefreshCw, Loader2, FileText, Edit, Trash2, Share2,
    Clock, MapPin, Monitor, Smartphone, Globe
} from 'lucide-react';
import api from '@/services/api';

const Activity = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [filterUser, setFilterUser] = useState('');
    const [dateRange, setDateRange] = useState('7d');
    const [currentPage, setCurrentPage] = useState(1);
    const [showFilters, setShowFilters] = useState(false);
    const itemsPerPage = 20;

    // Fetch activity logs
    const { data, isLoading, refetch } = useQuery({
        queryKey: ['adminActivity', currentPage, searchTerm, filterType, filterUser, dateRange],
        queryFn: async () => {
            const params = new URLSearchParams({
                page: currentPage,
                limit: itemsPerPage,
                search: searchTerm,
                type: filterType,
                userId: filterUser,
                range: dateRange
            });
            const response = await api.get(`/admin/activity-log?${params}`);
            return response.data;
        }
    });

    // Activity type configurations
    const activityTypes = {
        user_login: {
            icon: UserCheck,
            color: 'blue',
            label: 'User Login'
        },
        user_logout: {
            icon: UserCheck,
            color: 'gray',
            label: 'User Logout'
        },
        user_created: {
            icon: UserPlus,
            color: 'green',
            label: 'User Created'
        },
        user_updated: {
            icon: Edit,
            color: 'yellow',
            label: 'User Updated'
        },
        user_deleted: {
            icon: Trash2,
            color: 'red',
            label: 'User Deleted'
        },
        password_changed: {
            icon: Lock,
            color: 'purple',
            label: 'Password Changed'
        },
        password_reset: {
            icon: Lock,
            color: 'orange',
            label: 'Password Reset'
        },
        two_factor_enabled: {
            icon: Shield,
            color: 'green',
            label: '2FA Enabled'
        },
        two_factor_disabled: {
            icon: Shield,
            color: 'red',
            label: '2FA Disabled'
        },
        card_viewed: {
            icon: Eye,
            color: 'blue',
            label: 'Card Viewed'
        },
        vcard_downloaded: {
            icon: Download,
            color: 'green',
            label: 'vCard Downloaded'
        },
        card_shared: {
            icon: Share2,
            color: 'purple',
            label: 'Card Shared'
        },
        profile_updated: {
            icon: User,
            color: 'yellow',
            label: 'Profile Updated'
        },
        settings_updated: {
            icon: Settings,
            color: 'gray',
            label: 'Settings Updated'
        },
        email_sent: {
            icon: Mail,
            color: 'blue',
            label: 'Email Sent'
        }
    };

    const dateRangeOptions = [
        { value: '1h', label: 'Last hour' },
        { value: '24h', label: 'Last 24 hours' },
        { value: '7d', label: 'Last 7 days' },
        { value: '30d', label: 'Last 30 days' },
        { value: '90d', label: 'Last 90 days' },
        { value: 'all', label: 'All time' }
    ];

    const handleExport = async () => {
        try {
            const params = new URLSearchParams({
                search: searchTerm,
                type: filterType,
                userId: filterUser,
                range: dateRange
            });

            const response = await api.get(`/admin/activity-log/export?${params}`, {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `activity-log-${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to export activity logs:', error);
        }
    };

    const ActivityItem = ({ activity }) => {
        const config = activityTypes[activity.type] || {
            icon: ActivityIcon,
            color: 'gray',
            label: activity.type
        };
        const Icon = config.icon;

        const getDeviceIcon = () => {
            if (activity.metadata?.device?.type === 'mobile') return Smartphone;
            if (activity.metadata?.device?.type === 'tablet') return Monitor;
            return Monitor;
        };

        const DeviceIcon = getDeviceIcon();

        return (
            <div className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-start space-x-3">
                    <div className={`mt-0.5 p-2 rounded-full bg-${config.color}-100`}>
                        <Icon className={`h-4 w-4 text-${config.color}-600`} />
                    </div>

                    <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-gray-900">
                                {activity.description}
                            </p>
                            <span className="text-xs text-gray-500">
                {new Date(activity.timestamp).toLocaleString()}
              </span>
                        </div>

                        <div className="mt-1 flex items-center space-x-4 text-xs text-gray-500">
                            {activity.user && (
                                <span className="flex items-center">
                  <User className="h-3 w-3 mr-1" />
                                    {activity.user.name || activity.user.email}
                </span>
                            )}

                            {activity.ip && (
                                <span className="flex items-center">
                  <Globe className="h-3 w-3 mr-1" />
                                    {activity.ip}
                </span>
                            )}

                            {activity.metadata?.location && (
                                <span className="flex items-center">
                  <MapPin className="h-3 w-3 mr-1" />
                                    {activity.metadata.location}
                </span>
                            )}

                            {activity.metadata?.device && (
                                <span className="flex items-center">
                  <DeviceIcon className="h-3 w-3 mr-1" />
                                    {activity.metadata.device.browser} on {activity.metadata.device.os}
                </span>
                            )}
                        </div>

                        {activity.metadata?.details && (
                            <p className="mt-1 text-xs text-gray-600">
                                {activity.metadata.details}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const totalPages = Math.ceil((data?.total || 0) / itemsPerPage);

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Activity Log</h1>
                            <p className="mt-2 text-gray-600">
                                Monitor all system activity and user actions
                            </p>
                        </div>
                        <button
                            onClick={() => refetch()}
                            className="p-2 text-gray-600 hover:text-gray-700 bg-white border border-gray-300 rounded-md"
                        >
                            <RefreshCw className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                {/* Filters and Search */}
                <div className="bg-white rounded-lg shadow mb-6">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                            <div className="flex items-center space-x-3">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                        placeholder="Search activity..."
                                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                    />
                                </div>
                                <button
                                    onClick={() => setShowFilters(!showFilters)}
                                    className={`p-2 bg-white border rounded-md transition-colors ${
                                        showFilters
                                            ? 'text-primary-600 border-primary-300 bg-primary-50'
                                            : 'text-gray-600 border-gray-300 hover:text-gray-700'
                                    }`}
                                >
                                    <Filter className="h-5 w-5" />
                                </button>
                            </div>

                            <div className="flex items-center space-x-3">
                                <select
                                    value={dateRange}
                                    onChange={(e) => {
                                        setDateRange(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                >
                                    {dateRangeOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>

                                <button
                                    onClick={handleExport}
                                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                >
                                    <Download className="h-4 w-4 mr-2" />
                                    Export
                                </button>
                            </div>
                        </div>

                        {/* Filters Panel */}
                        {showFilters && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Activity Type
                                        </label>
                                        <select
                                            value={filterType}
                                            onChange={(e) => {
                                                setFilterType(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                            className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        >
                                            <option value="all">All Activities</option>
                                            {Object.entries(activityTypes).map(([key, config]) => (
                                                <option key={key} value={key}>
                                                    {config.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            User
                                        </label>
                                        <select
                                            value={filterUser}
                                            onChange={(e) => {
                                                setFilterUser(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                            className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        >
                                            <option value="">All Users</option>
                                            {data?.users?.map(user => (
                                                <option key={user._id} value={user._id}>
                                                    {user.name || user.email}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Activity List */}
                    <div className="divide-y divide-gray-200">
                        {isLoading ? (
                            <div className="px-6 py-12 text-center">
                                <Loader2 className="animate-spin h-8 w-8 text-primary-600 mx-auto" />
                            </div>
                        ) : data?.activities?.length === 0 ? (
                            <div className="px-6 py-12 text-center">
                                <ActivityIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                                <p className="text-gray-500">No activity found</p>
                            </div>
                        ) : (
                            data?.activities?.map((activity) => (
                                <ActivityItem key={activity._id} activity={activity} />
                            ))
                        )}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="px-6 py-4 border-t border-gray-200">
                            <div className="flex items-center justify-between">
                                <div className="text-sm text-gray-700">
                                    Showing {((currentPage - 1) * itemsPerPage) + 1} to{' '}
                                    {Math.min(currentPage * itemsPerPage, data?.total || 0)} of{' '}
                                    {data?.total || 0} activities
                                </div>
                                <div className="flex items-center space-x-2">
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                        disabled={currentPage === 1}
                                        className="p-2 text-gray-600 hover:text-gray-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                                    >
                                        <ChevronLeft className="h-5 w-5" />
                                    </button>

                                    {[...Array(totalPages)].map((_, i) => {
                                        const page = i + 1;
                                        if (
                                            page === 1 ||
                                            page === totalPages ||
                                            (page >= currentPage - 1 && page <= currentPage + 1)
                                        ) {
                                            return (
                                                <button
                                                    key={page}
                                                    onClick={() => setCurrentPage(page)}
                                                    className={`px-3 py-1 rounded ${
                                                        currentPage === page
                                                            ? 'bg-primary-600 text-white'
                                                            : 'text-gray-700 hover:bg-gray-100'
                                                    }`}
                                                >
                                                    {page}
                                                </button>
                                            );
                                        }
                                        if (page === currentPage - 2 || page === currentPage + 2) {
                                            return <span key={page}>...</span>;
                                        }
                                        return null;
                                    })}

                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                        disabled={currentPage === totalPages}
                                        className="p-2 text-gray-600 hover:text-gray-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                                    >
                                        <ChevronRight className="h-5 w-5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Activity Summary */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Total Activities</p>
                                <p className="mt-2 text-2xl font-semibold text-gray-900">
                                    {data?.total || 0}
                                </p>
                            </div>
                            <ActivityIcon className="h-8 w-8 text-gray-400" />
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Active Users</p>
                                <p className="mt-2 text-2xl font-semibold text-gray-900">
                                    {data?.stats?.activeUsers || 0}
                                </p>
                            </div>
                            <Users className="h-8 w-8 text-green-400" />
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Login Events</p>
                                <p className="mt-2 text-2xl font-semibold text-gray-900">
                                    {data?.stats?.loginEvents || 0}
                                </p>
                            </div>
                            <UserCheck className="h-8 w-8 text-blue-400" />
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Card Views</p>
                                <p className="mt-2 text-2xl font-semibold text-gray-900">
                                    {data?.stats?.cardViews || 0}
                                </p>
                            </div>
                            <Eye className="h-8 w-8 text-purple-400" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Activity;